import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Sidebar from '@/components/feature/Sidebar';
import Footer from '@/components/feature/Footer';
import Reveal from '@/components/feature/Reveal';
import { menuItems } from '@/mocks/menu';

export default function Home() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const featuredItems = useMemo(() => {
    return menuItems.filter((item) => item.popular).slice(0, 4);
  }, []);

  return (
    <div className="flex min-h-screen bg-background-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 overflow-auto">
          {/* Hero Banner */}
          <section className="relative w-full h-[440px] md:h-[560px] lg:h-[640px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Artisan%20coffee%20pouring%20latte%20art%20on%20marble%20counter%2C%20steam%20rising%2C%20warm%20golden%20lighting%2C%20premium%20cafe%20atmosphere%2C%20food%20photography%2C%20rich%20warm%20tones%2C%20professional%20coffee%20shop%20aesthetic%2C%20inviting%20cozy%20mood&width=1600&height=800&seq=home-hero-01&orientation=landscape"
              alt="BIOLA Cafe"
              className="w-full h-full object-cover object-center animate-hero-zoom"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/40 via-foreground-950/30 to-foreground-950/60" />
            <div className="absolute inset-0 flex items-center justify-center px-4">
              <div className="text-center max-w-3xl w-full">
                <span className="inline-block px-4 py-1.5 bg-accent-500/90 text-background-50 text-xs font-semibold rounded-full mb-4 animate-hero-content">
                  {t('hero.badge')}
                </span>
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight animate-hero-content-delay">
                  {t('hero.title')}
                </h1>
                <p className="text-background-100 text-base md:text-lg max-w-xl mx-auto leading-relaxed mb-6 animate-hero-content-delay-2">
                  {t('hero.subtitle')}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 animate-hero-content-delay-2">
                  <button
                    onClick={() => navigate('/menu')}
                    className="btn-press group inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-xl hover:bg-primary-600 whitespace-nowrap cursor-pointer w-full sm:w-auto"
                  >
                    <i className="ri-restaurant-2-line" />
                    {t('hero.ctaMenu')}
                  </button>
                  <button
                    onClick={() => navigate('/about')}
                    className="btn-press group inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 font-semibold rounded-xl hover:bg-background-50/30 border border-background-50/30 whitespace-nowrap cursor-pointer w-full sm:w-auto"
                  >
                    <i className="ri-information-line" />
                    {t('hero.ctaAbout')}
                  </button>
                  <button
                    onClick={() => navigate('/locations')}
                    className="btn-press group inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent-500 text-background-50 font-semibold rounded-xl hover:bg-accent-600 whitespace-nowrap cursor-pointer w-full sm:w-auto"
                  >
                    <i className="ri-map-pin-2-line" />
                    {t('hero.ctaLocation')}
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* About Teaser */}
          <Reveal>
            <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  {t('home.aboutTitle')}
                </h2>
                <p className="text-foreground-600 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
                  {t('home.aboutDesc')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 lg:gap-8 items-stretch">
                  {[
                    { icon: 'ri-leaf-line', title: t('about.fresh.title'), desc: t('about.fresh.desc') },
                    { icon: 'ri-heart-pulse-line', title: t('about.love.title'), desc: t('about.love.desc') },
                    { icon: 'ri-time-line', title: t('about.daily.title'), desc: t('about.daily.desc') },
                  ].map((feature, i) => (
                    <Reveal key={feature.title} delay={i * 90} className="h-full">
                      <div className="group bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70 hover:border-primary-300/40 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:shadow-primary-500/5 h-full flex flex-col">
                        <span className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-accent-100 text-accent-600 rounded-xl mx-auto mb-4 transition-transform duration-300 group-hover:scale-110">
                          <i className={`${feature.icon} text-xl md:text-2xl`} />
                        </span>
                        <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-foreground-500 text-sm leading-relaxed flex-1">
                          {feature.desc}
                        </p>
                      </div>
                    </Reveal>
                  ))}
                </div>
                <button
                  onClick={() => navigate('/about')}
                  className="btn-press group mt-8 inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-xl hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                >
                  {t('home.aboutCta')}
                  <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
                </button>
              </div>
            </section>
          </Reveal>

          {/* Menu Teaser - brief */}
          <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
            <div className="max-w-6xl mx-auto">
              <Reveal>
                <div className="text-center max-w-2xl mx-auto mb-8 md:mb-10">
                  <span className="inline-block px-4 py-1.5 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full mb-4">
                    {t('menuTeaser.badge')}
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                    {t('menuTeaser.title')}
                  </h2>
                  <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
                    {t('menuTeaser.desc')}
                  </p>
                </div>
              </Reveal>

              {/* Featured dishes */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-8 md:mb-10 items-stretch">
                {featuredItems.map((item, i) => (
                  <Reveal key={item.id} delay={i * 70} className="h-full">
                    <div
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300/60 hover:ring-1 hover:ring-primary-300/30 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg hover:shadow-primary-500/5 cursor-pointer h-full flex flex-col"
                      onClick={() => navigate('/menu')}
                    >
                      <div className="w-full aspect-square bg-background-100 overflow-hidden flex-shrink-0">
                        <img
                          src={item.image}
                          alt={t(`item.${item.id}.name`)}
                          className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                        />
                      </div>
                      <div className="p-3 md:p-4 flex-1 flex flex-col">
                        <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 mb-1 leading-snug truncate transition-colors duration-300 group-hover:text-primary-600">
                          {t(`item.${item.id}.name`)}
                        </h3>
                        <p className="text-primary-600 font-bold text-sm md:text-base mt-auto">
                          {i18n.language === 'ar' ? `${item.price} ${t('menu.currency')}` : `${t('menu.currency')} ${item.price}`}
                        </p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>

              <Reveal>
                <div className="text-center">
                  <button
                    onClick={() => navigate('/menu')}
                    className="btn-press group inline-flex items-center gap-2 px-7 py-3 bg-primary-500 text-background-50 font-semibold rounded-xl hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                  >
                    {t('menuTeaser.cta')}
                    <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
                  </button>
                </div>
              </Reveal>
            </div>
          </section>

          {/* Contact CTA */}
          <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-primary-500">
            <Reveal>
              <div className="max-w-2xl mx-auto text-center">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
                  {t('contact.title')}
                </h2>
                <p className="text-background-100 text-base md:text-lg leading-relaxed mb-6">
                  {t('contact.desc')}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="mailto:biola.ae1@gmail.com"
                    className="btn-press group inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-50 text-primary-600 font-semibold rounded-xl hover:bg-background-100 whitespace-nowrap w-full sm:w-auto"
                  >
                    <i className="ri-mail-line transition-transform duration-300 group-hover:-translate-y-0.5" />
                    biola.ae1@gmail.com
                  </a>
                  <button
                    onClick={() => navigate('/contact')}
                    className="btn-press group inline-flex items-center justify-center gap-2 px-6 py-3 bg-background-50/20 backdrop-blur-sm text-background-50 font-semibold rounded-xl hover:bg-background-50/30 border border-background-50/30 whitespace-nowrap cursor-pointer w-full sm:w-auto"
                  >
                    <i className="ri-send-plane-line transition-transform duration-300 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5" />
                    {t('hero.ctaContact')}
                  </button>
                </div>
              </div>
            </Reveal>
          </section>

          <Footer />
        </main>
      </div>
    </div>
  );
}